import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Loader2, Download } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getStudioTab, subscribeStudioTab, isNudgeSuppressed, requestStudioTab } from "@/lib/activity";

// Tools the assistant can route the user to directly (via a [[go:TOOL]] tag).
const ROUTES = {
  image: { path: "/studio", tab: "image" },
  video: { path: "/studio", tab: "video" },
  storyboard: { path: "/studio", tab: "storyboard" },
  audio: { path: "/studio", tab: "audio" },
  avatar: { path: "/studio", tab: "avatar" },
  "ad-studio": { path: "/studio", tab: "ad-studio" },
  editor: { path: "/editor" },
  gallery: { path: "/gallery" },
  pricing: { path: "/pricing" },
  brand: { path: "/brand" },
};
const GO_RE = /\[\[go:([a-z-]+)\]\]/i;
// Strip complete or partial routing tags (and any trailing "[") from display.
const stripGo = (t) => (t || "").replace(/\[\[go:[a-z-]*\]?\]?/gi, "").replace(/\s*\[+\s*$/, "").trimEnd();

function installHelp() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isSamsung = /SamsungBrowser/.test(ua);
  const isFirefox = /Firefox\/|FxiOS/.test(ua);
  const isChromeIOS = /CriOS/.test(ua);
  if (isIOS) {
    return (isFirefox || isChromeIOS)
      ? "On iPhone/iPad, open this page in Safari, tap the Share button, then “Add to Home Screen.”"
      : "On iPhone/iPad: tap the Share button (the square with an up-arrow) in Safari, then “Add to Home Screen.”";
  }
  if (isAndroid) {
    if (isSamsung) return "On Android (Samsung Internet): tap the menu (≡), then “Add page to → Home screen.”";
    if (isFirefox) return "On Android (Firefox): tap the menu (⋮), then “Install” / “Add to Home screen.”";
    return "On Android: tap the menu (⋮) in Chrome, then “Install app” / “Add to Home screen.”";
  }
  if (isFirefox) return "On desktop Firefox, open Nox AI in Chrome or Edge to install it as an app (Firefox doesn’t support installing web apps).";
  return "On desktop: click the install icon (a small screen/⊕ icon) at the right of the address bar, or open the browser menu and choose “Install Nox AI.”";
}

function buildWelcome() {
  const ua = navigator.userAgent || "";
  const isMobile = /iPhone|iPad|iPod|Android/.test(ua);
  return (
    "Welcome to Nox AI! 👋 I'm Nox, your studio assistant. Tell me what you want to make — an image, a video (with or without sound), a voiceover, a full ad, or even a long film — and I'll take you straight to the right tool.\n\n" +
    `💡 You can turn Nox AI into an app by saving us to your ${isMobile ? "home screen" : "desktop"}! ` +
    installHelp()
  );
}

function getSessionId() {
  let sid = localStorage.getItem("nox_assistant_session");
  if (!sid) {
    sid = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("nox_assistant_session", sid);
  }
  return sid;
}

const SUGGESTIONS = [
  "Analyze my website for ad ideas",
  "Which video model has sound?",
  "How do I add a voiceover?",
];

// Proactive quick-starts shown in the post-signup nudge bubble — each opens the
// assistant and pre-sends the query so the user lands on the right tool fast.
const NUDGE_ACTIONS = [
  { label: "🎬 Make an ad from my website", query: "Analyze my website for ad ideas" },
  { label: "🖼️ Create a product image", query: "How do I generate a great product image?" },
  { label: "🎥 A video with sound", query: "Which video model has sound and how do I make one?" },
];

// Contextual nudges: if a user lingers on a studio tab without generating, the
// assistant proactively offers help specific to that tool.
const TAB_NUDGES = {
  image: { msg: "Not sure which image model to pick? I can recommend one for your goal.", label: "🖼️ Help me choose an image model", query: "Which image model should I use and why?" },
  video: { msg: "Choosing a video model? I can tell you which ones have sound and which give the best value.", label: "🎥 Which video model is best?", query: "Which video model has sound and which is best value?" },
  storyboard: { msg: "Building a multi-scene video? I can walk you through the storyboard flow.", label: "🎬 How storyboards work", query: "How does the storyboard tool work?" },
  audio: { msg: "Need a voiceover or background music? I'll point you to the right option.", label: "🎙️ Voiceover & music help", query: "How do I add a voiceover or background music?" },
  avatar: { msg: "Making a talking avatar? Here's how to get the most natural result.", label: "🧑‍💼 Talking avatar tips", query: "How do I make a good talking avatar?" },
  "ad-studio": { msg: "Want a full ad or a promo video from your website? I can guide you.", label: "📣 Make an ad or site video", query: "How do I make an ad or a video from my website?" },
};

export function ChatAssistant() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [{ role: "assistant", content: buildWelcome() }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [nudge, setNudge] = useState(null); // { kind, text, actions, tab } | null
  const [installEvt, setInstallEvt] = useState(null);
  const shownTabs = useRef(new Set());
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => {
    if (open) scrollToBottom();
  }, [open, messages, scrollToBottom]);

  const navigateTo = useCallback((tool) => {
    const target = ROUTES[(tool || "").toLowerCase()];
    if (!target) return;
    const onPath = location.pathname === target.path;
    if (!onPath) navigate(target.path);
    if (target.tab) {
      if (onPath) requestStudioTab(target.tab);
      else setTimeout(() => requestStudioTab(target.tab), 300);
    }
  }, [location.pathname, navigate]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    let full = "";
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("Request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.delta) {
              full += data.delta;
              const shown = stripGo(full);
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: shown };
                return copy;
              });
              scrollToBottom();
            }
          } catch {}
        }
      }
      // Route the user to the recommended tool, if any.
      const m = full.match(GO_RE);
      if (m) navigateTo(m[1]);
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry — I couldn't reach the assistant. Please try again.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
      scrollToBottom();
    }
  };

  const markWelcomeSeen = () => {
    try {
      localStorage.setItem("nox_nudge_seen", "1");
      sessionStorage.removeItem("nox_just_signed_up");
    } catch {}
  };

  const dismissNudge = () => {
    if (nudge?.kind === "welcome") markWelcomeSeen();
    setNudge(null);
  };

  const openPanel = (seed) => {
    markWelcomeSeen();
    setNudge(null);
    setOpen(true);
    if (seed) send(seed);
  };

  // Automatic surfacing (welcome nudge, idle contextual nudge, and Studio
  // default-open) is intentionally disabled — the assistant only opens when the
  // user taps the launcher. Auto-routing on chat replies still works.

  // Capture the browser's install prompt so we can offer a one-tap "Install app".
  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); setInstallEvt(e); };
    const onInstalled = () => setInstallEvt(null);
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const doInstall = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    try { await installEvt.userChoice; } catch {}
    setInstallEvt(null);
  };

  if (!user) return null;

  return (
    <>
      {!open && (
        <>
          {nudge && (
            <div
              data-testid="assistant-nudge"
              className="fixed bottom-24 right-5 z-40 w-[300px] rounded-2xl border border-primary/25 bg-[#0B0C11] shadow-2xl shadow-primary/10 p-4
                animate-in fade-in slide-in-from-bottom-4 duration-500"
            >
              <button
                onClick={dismissNudge}
                data-testid="assistant-nudge-dismiss"
                aria-label="Dismiss"
                className="absolute top-2 right-2 h-6 w-6 rounded-md text-muted-foreground hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="flex items-start gap-2.5 pr-4">
                <div className="h-8 w-8 shrink-0 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <p className="text-sm text-white leading-snug">{nudge.text}</p>
              </div>
              <div className="mt-3 flex flex-col gap-1.5">
                {nudge.actions.map((a) => (
                  <button
                    key={a.label}
                    onClick={() => openPanel(a.query)}
                    data-testid="assistant-nudge-action"
                    className="text-left text-xs px-3 py-2 rounded-lg border border-white/10 text-white/90 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => openPanel()}
                data-testid="assistant-nudge-open"
                className="mt-2.5 w-full text-xs font-semibold text-primary hover:underline"
              >
                Or just ask me anything →
              </button>
            </div>
          )}
          <button
            onClick={() => openPanel()}
            data-testid="assistant-open-button"
            aria-label="Open studio assistant"
            className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-primary text-black shadow-lg shadow-primary/30 flex items-center justify-center hover:bg-[#00C2D9] transition-colors"
          >
            {nudge ? (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 border-2 border-[#0B0C11]" />
            ) : null}
            <MessageCircle className="h-6 w-6" />
          </button>
        </>
      )}

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed z-40 bg-[#0B0C11] border border-white/10 shadow-2xl flex flex-col
            inset-x-0 bottom-0 h-[80vh] rounded-t-2xl
            sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[560px] sm:w-[380px] sm:rounded-2xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-white">Nox Assistant</p>
                <p className="text-[10px] text-muted-foreground">Here to help you create</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="assistant-close-button"
              aria-label="Close assistant"
              className="h-8 w-8 rounded-lg text-muted-foreground hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div
            ref={scrollRef}
            data-testid="assistant-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`assistant-msg-${m.role}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "bg-primary text-black rounded-br-sm"
                      : "bg-white/5 text-white border border-white/10 rounded-bl-sm"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  ) : "")}
                </div>
              </div>
            ))}
          </div>

          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
              {installEvt && (
                <button
                  onClick={doInstall}
                  data-testid="assistant-install-button"
                  className="text-xs px-3 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary font-semibold hover:bg-primary/25 transition-colors inline-flex items-center gap-1.5"
                >
                  <Download className="h-3.5 w-3.5" /> Install Nox AI
                </button>
              )}
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid="assistant-suggestion"
                  className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-muted-foreground hover:text-white hover:border-primary/40 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="p-3 border-t border-white/10 shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask about any tool, or paste your website URL…"
                rows={1}
                data-testid="assistant-input"
                className="flex-1 resize-none max-h-28 bg-[#050508]/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
              />
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                data-testid="assistant-send-button"
                aria-label="Send message"
                className="h-10 w-10 shrink-0 rounded-xl bg-primary text-black flex items-center justify-center hover:bg-[#00C2D9] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
